import { Injectable, OnDestroy } from '@angular/core';
import { environment } from '@environments/environment';
import { GeneralSocket, NotificationSocket } from '@interfaces/socket';
import { User as DbUser } from '@interfaces/user';
import { User } from 'firebase/auth';
import { Socket, SocketIoConfig } from 'ngx-socket-io';
import { BehaviorSubject } from 'rxjs';
import { ProvinceService } from './province.service';
import { RestService } from './rest.service';
import * as pako from 'pako';
import { JourneyService } from './journey.service';

const configMain: SocketIoConfig = { url: environment.socketMainUrl, options: { transports: ['websocket'] } };
const configCalculator: SocketIoConfig = { url: environment.socketCalculatorUrl, options: { transports: ['websocket'] } };

@Injectable({
  providedIn: 'root'
})
export class SocketService implements OnDestroy {

  listnerMainInited = false;
  listnerCalculatorInited = false;
  user$: BehaviorSubject<DbUser | null> = new BehaviorSubject<DbUser | null>(null);

  private socketMain: Socket;
  private socketCalculator: Socket;

  constructor(
    private province: ProvinceService,
    private rest: RestService,
    private journeyService: JourneyService
  ) {
    this.socketMain = new Socket(configMain);
    this.socketCalculator = new Socket(configCalculator);
  }

  ngOnDestroy() {
    this.socketMain.disconnect();
    this.socketCalculator.disconnect();
  }

  async initSocket(user: User) {
    console.log('user', user);
    if (user) {
      const currentUser: DbUser | null = await this.rest.getCurrentUser();
      if (currentUser) {
        this.connectSocketMain(currentUser);
        this.connectSocketCalculator(currentUser);
      }
    } else {
      this.socketMain.disconnect();
      this.socketCalculator.disconnect();
    }
  }

  private connectSocketMain(user: DbUser) {
    const result = this.socketMain.connect();
    if (result.id) {
      this.registerMainUid(user);
    }

    if (!this.listnerMainInited) {
      this.listnerMainInited = true;
      this.socketMain.fromEvent('connect').subscribe(() => {
        console.log('Connected to socket main server! Socket ID:', this.socketMain.ioSocket.id);
        this.registerMainUid(user);
      });

      // List of listners
      this.listnerUpdateMainGeneral();
      this.listnerRealtimeUpdate();
    }
  }

  private connectSocketCalculator(user: DbUser) {
    const result = this.socketCalculator.connect();
    if (result.id) {
      this.registerCalculatorUid(user);
    }

    if (!this.listnerCalculatorInited) {
      this.listnerCalculatorInited = true;
      this.socketCalculator.fromEvent('connect').subscribe(() => {
        console.log('Connected to socket calculator server! Socket ID:', this.socketCalculator.ioSocket.id);
        this.registerCalculatorUid(user);
      });

      // List of listners
      this.listnerUpdateCalculatorGeneral();
    }
  }

  private registerMainUid(user: DbUser) {
    console.log('registerUser Main', user);
    this.socketMain.emit('set-uid', { uid: user.uid, provinceId: user.province?.id?.toString() });
  }

  private registerCalculatorUid(user: DbUser) {
    console.log('registerUser Calculator', user);
    if (user.role?.includes('admin')) {
      setTimeout(() => {
        this.province.allProvinces.forEach(province => {
          this.socketCalculator.emit('set-uid', { uid: user.uid, provinceId: province.id?.toString() });
        });
      }, 2000);
    } else {
      this.socketCalculator.emit('set-uid', { uid: user.uid, provinceId: user.province?.id?.toString() });
    }
  }

  private listnerUpdateCalculatorGeneral() {
    this.socketCalculator.on('province', async (generalInfo: any) => {
      // TODO: Remove tryCatch and remove second case later
      try {
        generalInfo = this.decompressData(generalInfo);
      } catch (error) {
        generalInfo.data = this.decompressData(generalInfo.data);
      }
      console.log('!!! generalInfo calculator !!!', generalInfo);
      if (generalInfo) {
        switch (generalInfo.type) {
          case 'ZONE_INFO':
            if (generalInfo.data) {
              this.province.updateZone(generalInfo.data);
            }
            break;
          default:
            console.error('Uknown socket general type');
            break;
        }
      }
    });
  }

  private listnerUpdateMainGeneral() {
    this.socketMain.on('province', async (generalInfo: any) => {
      try {
        generalInfo = this.decompressData(generalInfo);
      } catch (error) { }

      console.log('!!! generalInfo Main !!!', generalInfo);
      if (generalInfo) {
        switch (generalInfo.type) {

          case 'SERVICE_INFO':
            this.province.updateService(generalInfo.data, 'zone');
            break;

          case 'SERVICE_INFO_P':
            this.province.updateService(generalInfo.data, 'pending');
            break;

          default:
            console.error('Uknown socket general type');
            break;
        }
      }
    });
  }

  private listnerRealtimeUpdate() {
    this.socketMain.on('realtime', async (generalInfo: any) => {
      try {
        generalInfo = this.decompressData(generalInfo);
      } catch (error) { }

      console.log('!!! listnerRealtimeUpdate !!!', generalInfo);
      if (generalInfo) {
        // Actualizar lista normal de journeys
        await this.journeyService.setNewInfo(generalInfo);
        // 🔥 También actualizar lista de todos los servicios (sin eliminar)
        await this.journeyService.setAllJourneysInfo(generalInfo);
      }
    });
  }

  /**
 * Decompresses data received via socket. If the data is in ArrayBuffer format,
 * it decompresses and parses it into a GeneralSocket object.
 * If decompression fails, it returns the original data cast as GeneralSocket.
 *
 * @param {ArrayBuffer | GeneralSocket} socketInfo - The data to decompress, which can be an ArrayBuffer or already a GeneralSocket object.
 * @returns {GeneralSocket} - The decompressed and parsed data as a GeneralSocket object, or the original data if an error occurs.
 */
  private decompressData(socketInfo: ArrayBuffer) {
    // Check if the input is an instance of ArrayBuffer before attempting to decompress
    if (socketInfo instanceof ArrayBuffer) {
      try {
        const decompressed = pako.inflate(new Uint8Array(socketInfo), { to: 'string' });
        const decompressParsed = JSON.parse(decompressed);
        return decompressParsed;
      } catch (error) {
        console.error("Failed to decompress or parse the data:", error);
        // Optionally, throw the error to be handled by the caller
        console.error('Decompression or parsing failed.');
        throw new Error('Decompression or parsing failed.');
      }
    } else {
      console.error('is GeneralSocket');
      throw new Error('is GeneralSocket');
    }

    // If not ArrayBuffer or if there was an error, return the input as GeneralSocket
    // return socketInfo as GeneralSocket;
  }
}
